import { ExternalLink, PhoneOutgoing } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { comingUpDetail, comingUpGroups, comingUpMode, comingUpTime, todaySpan } from "@/lib/coming-up";
import { useCalendarCallbacks } from "@/lib/queries";
import type { CalendarCallback } from "@/lib/types";
import { useNow } from "@/lib/use-now";
import { cn } from "@/lib/utils";
import { useOutboundDial } from "@/softphone/manual-call";

/**
 * Coming up: the rep's callbacks for today on the Phone page between calls (ACL-671, ADR-226 in the
 * platform repo), read from the calendar's callbacks route (ACL-658) for the rep signed in, the
 * people of one accident together. Each row says the time on the rep's clock, who, their role and
 * what the call is for, the backup time, and how the call is made: the dialer brings it, or the
 * rep calls. Call now places the call through the campaign the callback names, as a hand dial does
 * (ADR-047), and Open goes to the record.
 *
 * Nothing is drawn with no callback today, so the idle stage reads as it did.
 */
export function ComingUp({ className }: { className?: string }) {
  const { user } = useAuth();
  const now = useNow(60_000);
  const span = React.useMemo(() => todaySpan(new Date(now)), [now]);
  const callbacks = useCalendarCallbacks(
    { ...span, user_ids: String(user?.id ?? ""), limit: 100 },
    { enabled: user !== null, refetchInterval: 60_000 },
  );
  const groups = comingUpGroups(callbacks.data ?? []);
  if (groups.length === 0) return null;
  const when = (iso: string) => comingUpTime(iso, new Date(now));
  return (
    <section aria-label="Coming up" className={className}>
      <p className="mb-2 text-2xs font-semibold uppercase tracking-wide text-text-subtle">Coming up · your callbacks today</p>
      <div className="space-y-3">
        {groups.map((group) => (
          <div key={group.accident ?? group.rows[0]!.id} className="rounded-lg border border-border">
            {group.accident && (
              <p className="border-b border-border bg-surface-sunken px-3 py-1.5 text-2xs text-text-muted">
                <span className="font-semibold text-text">Accident {group.accident}</span> · {group.who} ·{" "}
                {group.rows.length} {group.rows.length === 1 ? "callback" : "callbacks"}
              </p>
            )}
            <ul className="divide-y divide-border">
              {group.rows.map((row) => (
                <Row key={row.id} row={row} when={when} />
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

function Row({ row, when }: { row: CalendarCallback; when: (iso: string) => string }) {
  const toast = useToast();
  const dial = useOutboundDial();
  const [calling, setCalling] = React.useState(false);
  const mode = comingUpMode(row);
  const detail = comingUpDetail(row, when);
  const callNow = async () => {
    if (!row.phone) return;
    setCalling(true);
    try {
      await dial.place(row.phone, row.campaign_id ?? null, row.lead_id);
    } catch (e) {
      toast.error("Could not place the call", e instanceof ApiError ? e.message : "Please try again.");
    } finally {
      setCalling(false);
    }
  };
  return (
    <li className="flex items-center gap-3 px-3 py-2 text-xs">
      <span className={cn("w-24 shrink-0 font-semibold tabular", row.status === "due" ? "text-warning-11" : "text-text")}>
        {when(row.due_at)}
      </span>
      <span className="min-w-0 flex-1">
        <span className="font-medium text-text">{row.contact_name ?? "No name"}</span>
        {detail && <span className="text-text-muted"> · {detail}</span>}
        {row.note && <span className="block truncate text-text-muted">{row.note}</span>}
      </span>
      <Badge tone={mode.tone} size="sm">
        {mode.label}
      </Badge>
      {row.phone && (
        <Button type="button" variant="outline" size="sm" disabled={calling || dial.placing} onClick={() => void callNow()}>
          <PhoneOutgoing />
          Call now
        </Button>
      )}
      <Button variant="ghost" size="sm" asChild>
        <Link to={`/contacts/${row.lead_id}`} aria-label={`Open ${row.contact_name ?? "the record"}`}>
          <ExternalLink />
          Open
        </Link>
      </Button>
    </li>
  );
}
